import {
  LISTING_VENDORS,
  type ListingStatus,
  type ListingVendor,
} from "@loot-os/fulfillment-core/listing/types";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@loot-os/ui/components/sheet";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { AwaitingProcessedImageAlert } from "../../components/AwaitingProcessedImageAlert";
import { ListingStatusBadge } from "../../components/badges";
import { ErrorAlert } from "../../components/ErrorAlert";
import { RowDetailButton } from "../../components/RowDetailButton";
import { UntaggedSneakersAlert } from "../../components/UntaggedSneakersAlert";
import { centsToDollars, dashIfEmpty, formatDate } from "../../lib/format";
import { stockxListingSizes } from "../../lib/listingSizes";
import { LISTING_STATUS_LABELS } from "../../lib/listingStatus";
import { useTRPC } from "../../lib/trpc";
import { useStandingNotice } from "../../lib/useStandingNotice";

const PAGE_SIZE = 50;

/** Select has no unset value, so an explicit member stands for "no filter". */
const ANY = "__any";

/** Select.Root reads these to label the trigger; without them it shows the raw value. */
const VENDOR_LABELS: Record<string, string> = {
  [ANY]: "All vendors",
  ...Object.fromEntries(LISTING_VENDORS.map((vendor) => [vendor, vendor])),
};

const STATUS_LABELS: Record<string, string> = {
  [ANY]: "All statuses",
  ...LISTING_STATUS_LABELS,
};

const COLUMNS = [
  "",
  "Title",
  "Vendor",
  "Sizes",
  "Status",
  "Price",
  "Qty",
  "Category",
  "Marketplace",
  "Tenant",
  "Scraped",
] as const;

/** Every vendor listing, unfiltered by marketplace or status unless the form asks. */
export function ListingsPage() {
  const trpc = useTRPC();
  const [draftQuery, setDraftQuery] = useState("");
  const [query, setQuery] = useState("");
  const [vendor, setVendor] = useState<ListingVendor | typeof ANY>(ANY);
  const [status, setStatus] = useState<ListingStatus | typeof ANY>(ANY);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listings = useInfiniteQuery(
    trpc.platform.fulfillment.listings.search.infiniteQueryOptions(
      {
        limit: PAGE_SIZE,
        query: query || undefined,
        vendor: vendor === ANY ? undefined : vendor,
        status: status === ANY ? undefined : status,
      },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const untaggedSneakers = useQuery(
    trpc.platform.fulfillment.listings.untaggedStockxSneakers.queryOptions(),
  );
  const awaitingImage = useQuery(
    trpc.platform.fulfillment.listings.awaitingProcessedImage.queryOptions(),
  );
  // Side checks of the page, so a failure is a toast (AGENTS.md); the listings still load.
  useStandingNotice(untaggedSneakers.error !== null, {
    id: "listings-untagged-sneakers-error",
    message: `Could not check for untagged StockX sneakers: ${untaggedSneakers.error?.message ?? ""}`,
  });
  useStandingNotice(awaitingImage.error !== null, {
    id: "listings-awaiting-processed-image-error",
    message: `Could not check for listings waiting for a processed image: ${awaitingImage.error?.message ?? ""}`,
  });
  const rows = listings.data?.pages.flatMap((page) => page.listings) ?? [];
  // Held by id, not by value: a refetch or a filter change replaces the row objects.
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Listings</h1>
        <p className="text-sm text-muted-foreground">
          Vendor listings as they were last scraped. Prices are acquisition cost in USD, before
          markup.
        </p>
      </div>

      <form
        className="flex flex-wrap items-center gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draftQuery.trim());
        }}
      >
        <Input
          aria-label="Listing title or id"
          className="w-72"
          placeholder="Title or id contains…"
          value={draftQuery}
          onChange={(event) => setDraftQuery(event.target.value)}
        />
        <Select
          items={VENDOR_LABELS}
          value={vendor}
          onValueChange={(value) => setVendor(value as ListingVendor)}
        >
          <SelectTrigger size="sm" className="w-40" aria-label="Vendor">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(VENDOR_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          items={STATUS_LABELS}
          value={status}
          onValueChange={(value) => setStatus(value as ListingStatus)}
        >
          <SelectTrigger size="sm" className="w-40" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" disabled={listings.isFetching}>
          {listings.isFetching ? "Loading…" : "Search"}
        </Button>
      </form>

      {listings.error && <ErrorAlert message={listings.error.message} />}
      {untaggedSneakers.data && untaggedSneakers.data.count > 0 && (
        <UntaggedSneakersAlert {...untaggedSneakers.data} />
      )}
      {awaitingImage.data && awaitingImage.data.total > 0 && (
        <AwaitingProcessedImageAlert {...awaitingImage.data} />
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {formatNumber(rows.length)}
            {listings.hasNextPage ? "+" : ""} loaded
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {listings.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No listings match these filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {COLUMNS.map((column) => (
                      <TableHead key={column}>{column}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((listing) => (
                    <TableRow
                      key={listing.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedId(listing.id)}
                    >
                      <TableCell>
                        {listing.image ? (
                          <img
                            src={listing.image}
                            alt=""
                            loading="lazy"
                            className="size-10 rounded object-contain"
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <RowDetailButton onSelect={() => setSelectedId(listing.id)}>
                          <div className="w-72">
                            <span className="block truncate">{listing.title}</span>
                            <span className="block truncate font-mono text-xs text-muted-foreground">
                              {listing.id}
                            </span>
                          </div>
                        </RowDetailButton>
                      </TableCell>
                      <TableCell>{listing.vendor}</TableCell>
                      <TableCell>
                        <ListingSizes sizes={stockxListingSizes(listing)} />
                      </TableCell>
                      <TableCell>
                        <ListingStatusBadge status={listing.status} />
                      </TableCell>
                      <TableCell>{centsToDollars(listing.price)}</TableCell>
                      <TableCell>{formatNumber(listing.availableQty)}</TableCell>
                      <TableCell>{dashIfEmpty(listing.category)}</TableCell>
                      <TableCell>{listing.onMarketplace ? "Yes" : "No"}</TableCell>
                      <TableCell>{listing.tenantId ?? "Shared"}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(listing.scrapedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {listings.hasNextPage && (
            <Button
              type="button"
              variant="outline"
              className="self-start"
              disabled={listings.isFetchingNextPage}
              onClick={() => void listings.fetchNextPage()}
            >
              {listings.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          )}
        </CardContent>
      </Card>

      <Sheet
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      >
        <SheetContent className="w-full gap-0 overflow-y-auto data-[side=right]:sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle className="text-base">{selected?.title}</SheetTitle>
            <SheetDescription className="font-mono text-xs">{selected?.id}</SheetDescription>
          </SheetHeader>
          {/* The search result is the whole row, so the drawer needs no second read. */}
          <pre className="px-4 pb-6 font-mono text-xs break-words whitespace-pre-wrap">
            {JSON.stringify(selected, null, 2)}
          </pre>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** A StockX listing's sizes on one line; the full list is in the hover title and the drawer. */
function ListingSizes({ sizes }: { sizes: string | null }) {
  return (
    <span className="block w-48 truncate" title={sizes ?? undefined}>
      {dashIfEmpty(sizes)}
    </span>
  );
}
